// src/components/ui/MetricCard.jsx
import React from 'react';
import { CardContent, Typography, Box, useTheme } from '@mui/material';
import { Card } from '../../design-system';

/**
 * Component for displaying a metric in a card format
 * @param {Object} props
 * @param {string} props.title - Metric title
 * @param {number|string} props.value - Metric value
 * @param {string} props.format - Format type ('number', 'percentage')
 * @param {React.ReactNode} props.icon - Optional icon to display
 * @param {string} props.color - Optional color theme ('primary', 'secondary', 'success', 'warning', 'error', 'info')
 * @param {Function} props.onClick - Optional click handler
 * @param {boolean} props.compact - Optional compact mode for smaller display
 */
const MetricCard = ({ 
  title, 
  value, 
  format = 'number', 
  icon, 
  color = 'primary', 
  onClick,
  compact = true 
}) => {
  const theme = useTheme();
  
  const formatValue = () => {
    if (format === 'percentage') {
      return `${parseFloat(value).toFixed(2)}%`;
    }
    return parseInt(value).toLocaleString() || '0';
  };

  const getColorValue = () => {
    const colorMap = {
      primary: theme.palette.primary.main,
      secondary: theme.palette.secondary.main,
      success: theme.palette.success.main,
      warning: theme.palette.warning.main,
      error: theme.palette.error.main,
      info: theme.palette.info.main,
    };
    return colorMap[color] || theme.palette.primary.main;
  };

  return (
    <Card 
      level={1} 
      sx={{ 
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s ease-in-out',
        '&:hover': onClick ? {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[4]
        } : {}
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: compact ? 2 : 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ flex: 1 }}>
            <Typography 
              variant={compact ? "caption" : "subtitle2"} 
              color="textSecondary" 
              gutterBottom
              sx={{ fontSize: compact ? '0.75rem' : '0.875rem' }}
            >
              {title}
            </Typography>
            <Typography 
              variant={compact ? "h5" : "h4"}
              sx={{ 
                fontSize: compact ? { xs: '1.25rem', sm: '1.5rem' } : { xs: '1.75rem', sm: '2.125rem' },
                fontWeight: 600,
                color: getColorValue()
              }}
            >
              {formatValue()}
            </Typography>
          </Box>
          {icon && (
            <Box 
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: compact ? 40 : 48,
                height: compact ? 40 : 48,
                borderRadius: '50%',
                backgroundColor: theme.palette.action.hover,
                color: getColorValue(),
                ml: 2
              }}
            >
              {React.cloneElement(icon, { 
                sx: { fontSize: compact ? 20 : 24 } 
              })}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default MetricCard;
